<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { datadogRum } from '@datadog/browser-rum'

import TheSideMenu from '@/components/organisms/TheSideMenu/index.vue'
import AMobileHeader from '@/components/organisms/ALayoutHeader/components/AMobileHeader/index.vue'
import AButton from '@/components/atoms/AButton/index.vue'
import { useAuthStore } from '@/stores/authStore'
import { useCustomerSideMenu } from '@/composables/useCustomerSideMenu'
import { getAuthCompanyAndUser } from '@/composables/api/customer/Auth'
import { getInitialUserDisplayMode } from '@/utils/UserDisplayMode'
import { UserDisplayMode } from '@/constants/enums/UserDisplayMode'
import { SideMenuDisplayMode } from '@/constants/enums/SideMenuDisplayMode'

/**
 * Composables
 */
const { t, locale } = useI18n()
const {
  sideMenuDisplayMode,
  isOpenedSideMenu,
  isDisplayedSideMenu,
  contentElement,
} = useCustomerSideMenu(watch)

/**
 * Stores
 */
const authStore = useAuthStore()

/**
 * Refs
 */
const menus = ref()
const isBuyer = ref(false)

/**
 * Watchers
 */
watch(
  locale,
  () => {
    menus.value = {
      evaluation: {
        title: t('common.evaluation.dashboard'),
        link: '/mypage/evaluation/dashboard',
      },
      dashboard: {
        title: t('common.dashboard.title'),
        link: '/mypage/dashboard',
      },
      upload: {
        title: t('common.upload.title'),
        link: '/mypage/upload/common',
      },
      scorecard: {
        title: t('common.scorecard.title'),
        link: '/mypage/scorecard',
      },
      improvementplans: {
        title: t('common.improvementplans.title'),
        link: '/mypage/improvementplans',
      },
      buyerImprovementplans: {
        title: t('common.improvementplans.title'),
        link: '/mypage/evaluation/improvementplans',
      },
      esgDataShare: {
        title: t('esg_data.main_title'),
        subPages: {
          answer: {
            title: t('esg_data.sub_title_answer'),
            link: '/mypage/datashare',
          },
          share: {
            title: t('esg_data.sub_title_share'),
            link: '/mypage/datashare/company',
          },
        },
      },
      buyerEsgDataShare: {
        title: t('esg_data.main_title'),
        link: '/mypage/buyer/datashare',
      },
      settings: {
        title: t('common.settings.title'),
        link: '/mypage/settings/account',
      },
      contact: {
        title: t('common.contact.title'),
        link: 'https://asuene-esg.zendesk.com/hc/ja',
      },
      logout: {
        title: t('common.logout.title'),
      },
    }
  },
  { immediate: true },
)

/**
 * Computed
 */
const topPagePath = computed<string>(() => {
  if (isBuyer.value) {
    return authStore.company?.contractPlans.guestBuyer
      ? '/mypage/buyer/datashare'
      : '/mypage/evaluation/dashboard'
  }
  return '/mypage/dashboard'
})

const isMobile = computed(() => {
  return sideMenuDisplayMode.value === SideMenuDisplayMode.SWITCH
})

/**
 * Lifecycle
 */
onMounted(async () => {
  const response = await getAuthCompanyAndUser()
  if (response.status.value === 'success') {
    isBuyer.value
      = getInitialUserDisplayMode(response.data.value.company.contractPlans)
        === UserDisplayMode.BUYER

    authStore.setCompany(response.data.value.company)
    authStore.setUser(response.data.value.user)
    authStore.setUserDisplayMode(
      getInitialUserDisplayMode(response.data.value.company.contractPlans),
    )

    const firstName = response.data.value.user?.firstName
    const lastName = response.data.value.user?.lastName
    const username
      = firstName && lastName ? `${firstName} ${lastName}` : undefined

    datadogRum.setUser({
      id: response.data.value.user.id,
      name: username,
      segment: 'user',
      company_id: response.data.value.company.id,
      company_name: response.data.value.company.name,
    })
  }
})
</script>

<template>
  <v-container
    class="error-page-layout"
    :data-side-menu-display-mode="sideMenuDisplayMode"
    fill-height
    fluid
  >
    <TheSideMenu
      v-model="isOpenedSideMenu"
      :class="{ 'active': isOpenedSideMenu, 'not-active': !isDisplayedSideMenu }"
      :is-under-break-point="isMobile"
      :menus="menus"
    />

    <div class="error-page-page">
      <AMobileHeader
        v-if="isMobile"
        v-model="isOpenedSideMenu"
        :is-guest="false"
        :side-menu-display-mode="sideMenuDisplayMode"
      />

      <div
        v-else
        class="error-page-header"
      />

      <div
        ref="contentElement"
        class="error-page-content"
      >
        <div>
          <p>{{ $t('common.error_page.section_one') }}</p>
          <p>{{ $t('common.error_page.section_two') }}</p>
        </div>

        <NuxtLink :to="topPagePath">
          <AButton size="large">{{ $t('common.sentence.back_to_top') }}</AButton>
        </NuxtLink>
      </div>
    </div>
  </v-container>
</template>

<style lang="scss" scoped>
.error-page-layout {
  display: flex;
  height: 100%;
  padding: 0 0 0 $sidemenu-width;
  color: rgb(var(--v-theme-ui-text-black));

  .active {
    transition: transform 0.3s ease;
    transform: translateX(0);
  }

  .not-active {
    transition: transform 0.3s ease;
    transform: translateX(-100%);
  }

  .error-page-page {
    width: 100%;
    height: 100vh;
    background-color: rgb(var(--v-theme-secondary-lighten-2));

    .error-page-header {
      height: 60px;
      background-color: rgb(var(--v-theme-primary));
    }

    .error-page-content {
      display: flex;
      flex-direction: column;
      gap: 24px;
      align-items: center;
      justify-content: center;
      height: calc(100% - 60px);
      text-align: center;
      background: rgb(var(--v-theme-ui-background));
    }
  }

  :deep(.rounded-lg) {
    border-radius: 12px !important;
  }

  :deep(.mypage-sidemenu) {
    // エラーページはサイドメニューのアクティブハイライトを常にオフ
    .v-list-item.active {
      background-color: transparent;
    }

    .v-list-item.active:hover {
      background-color: rgba((var(--v-theme-ui-white)), 0.25);
    }
  }
}

.v-container {
  &[data-side-menu-display-mode='switch'] {
    padding: 0;
  }
}
</style>
